import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  BookOpen, Search, MessageSquare, Send, Loader2, ChevronRight,
  Users, FileText, CreditCard, FolderKanban, LineChart, Share2,
  Shield, ScrollText, Bot, Settings, ArrowLeft, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const MODULES = [
  {
    id: "funnel",
    icon: Sparkles,
    title: "Funnel Commercial Complet",
    color: "#0099CC",
    roles: ["admin"],
    summary: "Le workflow automatise de A a Z : Lead -> Projet",
    steps: [
      "1. LEADS : Les prospects arrivent via les formulaires du site public (devis/audit). Ils apparaissent automatiquement dans la page Leads",
      "2. PROPOSITION : Cliquez 'Creer une proposition' sur un lead. Le systeme convertit automatiquement le lead en client et cree un compte utilisateur avec email de bienvenue",
      "3. CONFIGUREZ : Choisissez la base de projet, les options mensuelles, appliquez une remise si necessaire. Le prix est calcule en temps reel",
      "4. ENVOI : Cliquez 'Envoyer par email'. Le client recoit un email avec ses identifiants de connexion et un lien vers la proposition",
      "5. SIGNATURE : Le client se connecte, consulte la proposition et la signe avec son nom complet",
      "6. AUTOMATISATION : A la signature, le systeme cree automatiquement : un Contrat, un Projet, une Facture et la tarification client",
      "7. PAIEMENT : Le client peut payer sa facture via le lien Stripe securise",
    ],
    tips: "Tout le workflow est automatise. De la reception du lead au lancement du projet, vous n'avez qu'a creer la proposition et l'envoyer. Le reste se fait tout seul !",
  },
  {
    id: "leads",
    icon: Users,
    title: "Leads",
    color: "#0099CC",
    roles: ["admin"],
    summary: "Gestion des leads entrants depuis le site public",
    steps: [
      "Les leads arrivent automatiquement via les formulaires de devis/audit du site public",
      "Consultez tous vos leads dans la page Leads avec leur statut (Nouveau, Contacte, Qualifie, Gagne, Perdu)",
      "Changez le statut d'un lead via le menu deroulant sur chaque carte",
      "Cliquez 'Convertir en client' pour creer automatiquement un compte client + utilisateur avec email de bienvenue",
      "Cliquez 'Creer une proposition' pour convertir le lead ET creer une proposition brouillon directement",
      "Envoyez un questionnaire IA au prospect pour qualifier son projet avant de creer la proposition",
    ],
    tips: "Utilisez le workflow complet : Lead -> Proposition -> Signature -> Contrat + Projet + Facture automatiques",
  },
  {
    id: "clients",
    icon: Users,
    title: "Clients",
    color: "#10B981",
    roles: ["admin"],
    summary: "Gestion de votre portefeuille clients",
    steps: [
      "Visualisez tous vos clients avec leurs informations dans la page Clients",
      "Chaque client possede : nom, email, telephone, adresse, ville, secteur, plan",
      "Un compte utilisateur est automatiquement cree pour chaque nouveau client",
      "Cliquez sur 'Voir la vitrine' pour acceder rapidement au site web du client",
      "Modifiez les informations du client a tout moment depuis sa fiche",
    ],
    tips: "Les clients sont crees automatiquement lors de la conversion d'un lead ou de la creation d'une proposition depuis un lead",
  },
  {
    id: "propositions",
    icon: FileText,
    title: "Propositions",
    color: "#F59E0B",
    roles: ["admin"],
    summary: "Creation et suivi des propositions commerciales",
    steps: [
      "Creez une nouvelle proposition depuis la page Propositions ou directement depuis un lead",
      "Choisissez la source : 'Client existant' ou 'Depuis un lead' (boutons en haut du formulaire)",
      "Si vous selectionnez un lead, le systeme cree automatiquement le client et son compte utilisateur",
      "Selectionnez une base de projet (obligatoire), ajoutez des options mensuelles, configurez une remise",
      "Le prix est calcule en temps reel dans le recapitulatif",
      "Envoyez la proposition par email au client avec le bouton 'Envoyer par email'",
      "Le client recoit un email avec ses identifiants de connexion et un lien vers la proposition",
      "Le client peut approuver puis signer la proposition dans son espace",
      "A la signature : creation automatique du contrat, projet, facture et tarification client",
      "Telechargez le devis en PDF a tout moment avec le bouton PDF",
    ],
    tips: "Le workflow complet est automatise : signature -> contrat + projet + facture + tarification crees automatiquement. Aucune action manuelle requise apres l'envoi !",
  },
  {
    id: "contrats",
    icon: ScrollText,
    title: "Contrats & Documents",
    color: "#8B5CF6",
    roles: ["admin", "client"],
    summary: "Creation, signature et gestion des contrats de services",
    steps: [
      "Les contrats sont crees automatiquement lors de la signature d'une proposition",
      "Creez des contrats libres : cliquez 'Nouveau contrat' dans la page Contrats",
      "Utilisez les modeles pre-remplis : cliquez 'Depuis un modele' pour creer en 2 clics (Maintenance, SEO, Site vitrine, Social media, E-commerce)",
      "Generez le PDF du contrat avec le bouton PDF ou Imprimer",
      "Envoyez le contrat par email au client avec le bouton Email (PDF en piece jointe)",
      "E-signature : cliquez 'E-signature' pour envoyer un lien securise au client (valable 7 jours). Le client signe sans se connecter",
      "Signature manuelle : uploadez le scan du contrat signe avec le bouton 'Signe manuellement'",
      "Le statut du contrat se met a jour automatiquement : Brouillon > Envoye > Signe",
    ],
    tips: "Utilisez les modeles pour gagner du temps ! 5 modeles couvrent 90% des cas : Maintenance (200 EUR), SEO (500 EUR), Site vitrine (2500 EUR), Social media (400 EUR), E-commerce (5000 EUR)",
  },
  {
    id: "factures",
    icon: CreditCard,
    title: "Factures & Paiements",
    color: "#EF4444",
    roles: ["admin", "client"],
    summary: "Suivi des factures et paiements Stripe",
    steps: [
      "Les factures sont creees automatiquement lors de la signature d'une proposition ou d'un contrat",
      "Statuts : En attente, Payee, En retard",
      "Generez un lien de paiement Stripe depuis la page client",
      "Le client recoit un email avec le lien de paiement securise",
      "Les relances sont envoyees automatiquement pour les factures en retard",
      "Suivez toutes les transactions dans le dashboard",
    ],
    tips: "L'integration Stripe gere les paiements en ligne de maniere securisee",
  },
  {
    id: "projets",
    icon: FolderKanban,
    title: "Projets",
    color: "#06B6D4",
    roles: ["admin", "client"],
    summary: "Gestion des projets clients",
    steps: [
      "Les projets sont crees automatiquement lors de la signature d'une proposition",
      "Informations : nom, URL, secteur, villes cibles, tags, statut",
      "Statuts : Actif, Maintenance, Archive",
      "Des captures d'ecran du site sont prises automatiquement",
      "Chaque projet est lie a un client et peut avoir des tickets associes",
    ],
    tips: "Le projet herite automatiquement du nom et du type de la proposition signee",
  },
  {
    id: "seo",
    icon: LineChart,
    title: "SEO & Rapports",
    color: "#22C55E",
    roles: ["admin", "client"],
    summary: "Donnees Google Analytics et Search Console",
    steps: [
      "Connectez le compte Google du client via OAuth dans la page SEO",
      "Les donnees Google Analytics 4 (sessions, utilisateurs, taux de rebond) sont synchronisees",
      "Les donnees Google Search Console (clics, impressions, CTR, position) sont recuperees",
      "Consultez l'historique des snapshots SEO pour suivre l'evolution",
      "Les rapports sont accessibles par le client dans son espace",
    ],
    tips: "Assurez-vous que le client a donne acces a ses proprietes GA4 et GSC",
  },
  {
    id: "reseaux-sociaux",
    icon: Share2,
    title: "Reseaux Sociaux",
    color: "#E91E63",
    roles: ["admin", "client"],
    summary: "Gestion et publication automatique sur les reseaux sociaux",
    steps: [
      "Gerez les publications sur Facebook, Instagram, LinkedIn et TikTok",
      "Utilisez le calendrier de publication pour planifier vos posts",
      "La generation de contenu IA vous aide a creer des textes engageants",
      "Planifiez vos posts : ils sont publies AUTOMATIQUEMENT a l'heure prevue",
      "Le systeme verifie toutes les 60 secondes s'il y a des posts a publier",
    ],
    tips: "Les posts planifies sont publies automatiquement ! Plus besoin de revenir pour cliquer 'Publier'",
  },
  {
    id: "concurrents",
    icon: Shield,
    title: "Concurrents SEO",
    color: "#FF6B35",
    roles: ["admin"],
    summary: "Analyse de la concurrence",
    steps: [
      "Ajoutez les URLs des sites concurrents a surveiller",
      "Comparez les metriques SEO avec vos propres performances",
      "Identifiez les opportunites et les menaces",
    ],
    tips: "Surveillez regulierement vos concurrents pour adapter votre strategie SEO",
  },
  {
    id: "tickets",
    icon: MessageSquare,
    title: "Tickets & Support",
    color: "#F97316",
    roles: ["admin", "client"],
    summary: "Systeme de tickets avec conversation et analyse IA",
    steps: [
      "Les clients creent des tickets depuis leur espace (type: Bug, Amelioration, Question, Urgence)",
      "Cliquez sur un ticket dans la liste pour ouvrir la vue conversation",
      "Repondez directement dans le fil de discussion avec texte et pieces jointes",
      "Le statut passe automatiquement a 'En cours' quand l'admin repond",
      "Le client recoit un email a chaque reponse de l'admin (et inversement)",
      "Cliquez 'Analyser IA' pour obtenir : categorie, resume, urgence et 3 suggestions de reponse",
      "Cliquez sur une suggestion IA pour la pre-remplir dans le champ de reponse",
      "Gerez le statut : Ouvert > En cours > Resolu > Ferme",
    ],
    tips: "L'analyse IA categorise automatiquement le ticket et vous propose des reponses adaptees. Gagnez du temps sur les demandes recurrentes !",
  },
  {
    id: "messagerie",
    icon: MessageSquare,
    title: "Messagerie directe",
    color: "#06B6D4",
    roles: ["admin", "client"],
    summary: "Messagerie en temps reel avec les clients",
    steps: [
      "Accedez a la Messagerie depuis le menu lateral (admin et client)",
      "Creez une nouvelle conversation : cliquez 'Nouveau' et selectionnez un client",
      "Envoyez des messages texte et des pieces jointes",
      "Les messages non lus s'affichent avec un badge dans le menu",
      "Le badge se rafraichit automatiquement toutes les 30 secondes",
      "Le client recoit un email a chaque nouveau message de l'admin",
      "L'admin recoit un email quand un client repond",
    ],
    tips: "Utilisez la messagerie pour les echanges rapides et informels. Pour les demandes formelles, orientez vers les tickets",
  },
  {
    id: "abonnements",
    icon: CreditCard,
    title: "Abonnements & Suspensions",
    color: "#EF4444",
    roles: ["admin"],
    summary: "Gestion automatique des abonnements et impays",
    steps: [
      "Les abonnements sont crees automatiquement lors de la souscription a un plan",
      "Suspension automatique : les abonnements avec factures impayees depuis +30 jours sont suspendus",
      "Le client recoit un email l'informant de la suspension",
      "L'admin est notifie par email de chaque suspension",
      "Pour reactiver : allez dans la fiche client et cliquez 'Reactiver l'abonnement'",
      "Verification manuelle : utilisez le bouton 'Verifier les impayes' dans la page Admin",
      "Le paiement Stripe est mis en pause automatiquement pendant la suspension",
    ],
    tips: "Le systeme verifie automatiquement les impayes. Les clients sont notifies par email avant la suspension",
  },
  {
    id: "assistant",
    icon: Bot,
    title: "Assistant IA",
    color: "#9333EA",
    roles: ["admin", "client"],
    summary: "Chatbot intelligent pour accompagner les clients",
    steps: [
      "L'assistant IA repond aux questions des clients sur leur projet",
      "Le contexte du client et de ses projets est automatiquement fourni a l'IA",
      "L'assistant peut donner des recommandations SEO, techniques et marketing",
      "Chaque client a ses propres fils de conversation",
    ],
    tips: "L'assistant utilise le contexte reel du client pour des reponses personnalisees",
  },
  {
    id: "parametrages",
    icon: Settings,
    title: "Parametrages & Admin",
    color: "#64748B",
    roles: ["admin"],
    summary: "Configuration de la plateforme et outils d'administration",
    steps: [
      "Plans & Tarifs : creez et gerez vos offres commerciales",
      "Affilies : programme d'affiliation pour la recommandation",
      "Agences : gestion multi-agences partenaires",
      "Suivi Emails : historique de tous les emails envoyes",
      "Inbox Projets : importation et validation de projets depuis GitHub (webhook configure)",
      "Nettoyage : endpoint /api/admin/cleanup-test-data pour purger les donnees de test",
      "Langues email : definissez la langue preferee de chaque client (FR/EN) pour les emails automatiques",
      "Verifier les impayes : declenchez manuellement la verification des abonnements en retard",
    ],
    tips: "Configurez vos plans tarifaires avant de creer des propositions. Activez le webhook GitHub pour importer automatiquement vos projets Emergent",
  },
];

function ModuleCard({ mod, onClick }) {
  const Icon = mod.icon;
  return (
    <button onClick={onClick}
      className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5 text-left hover:border-opacity-60 transition-all group"
      style={{ borderColor: `${mod.color}20` }}
      data-testid={`guide-module-${mod.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${mod.color}15` }}>
          <Icon className="h-5 w-5" style={{ color: mod.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold group-hover:text-[#0099CC] transition-colors">{mod.title}</p>
          <p className="text-[#94A3B8] text-sm mt-1 line-clamp-2">{mod.summary}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-[#64748b] group-hover:text-[#0099CC] transition-colors shrink-0 mt-1" />
      </div>
    </button>
  );
}

function ModuleDetail({ mod, onBack }) {
  const Icon = mod.icon;
  return (
    <div className="space-y-6 animate-fade-in" data-testid={`guide-detail-${mod.id}`}>
      <Button variant="ghost" size="sm" onClick={onBack}
        className="text-[#94A3B8] hover:text-white" data-testid="guide-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1.5" />Retour au guide
      </Button>

      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${mod.color}15` }}>
          <Icon className="h-6 w-6" style={{ color: mod.color }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">{mod.title}</h2>
          <p className="text-[#94A3B8] mt-0.5">{mod.summary}</p>
        </div>
      </div>

      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <BookOpen className="h-4 w-4" style={{ color: mod.color }} />
          Etapes d'utilisation
        </h3>
        <div className="space-y-3">
          {mod.steps.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
                style={{ backgroundColor: `${mod.color}20`, color: mod.color }}>
                {i + 1}
              </div>
              <p className="text-[#94A3B8] text-sm leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {mod.tips && (
        <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-4 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-[#0099CC] shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium text-sm mb-1">Astuce</p>
            <p className="text-[#94A3B8] text-sm">{mod.tips}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function AiChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `guide-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/guide/ask`, { question: q, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.answer }]);
    } catch {
      toast.error("Erreur de l'assistant IA");
      setMessages(prev => [...prev, { role: "assistant", text: "Desole, une erreur s'est produite. Veuillez reessayer." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl flex flex-col h-[500px]" data-testid="guide-ai-chat">
      <div className="p-4 border-b border-[#2A3441] flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#0099CC]/15 flex items-center justify-center">
          <Bot className="h-4 w-4 text-[#0099CC]" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Assistant Guide IA</p>
          <p className="text-[#64748b] text-xs">Posez vos questions sur l'application</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
            <p className="text-[#94A3B8] text-sm">Posez une question, par exemple :</p>
            <div className="mt-3 space-y-2">
              {[
                "Comment creer une proposition depuis un lead ?",
                "Comment faire signer un contrat ?",
                "Comment connecter Google Analytics ?",
                "Comment generer une facture ?",
              ].map((q, i) => (
                <button key={i} onClick={() => { setInput(q); }}
                  className="block mx-auto text-xs text-[#0099CC] hover:text-[#0099CC]/80 bg-[#0099CC]/5 px-3 py-1.5 rounded-full transition-colors"
                  data-testid={`suggestion-${i}`}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
              msg.role === "user"
                ? "bg-[#0099CC] text-white"
                : "bg-[#0B0F19] border border-[#2A3441] text-[#94A3B8]"
            }`}>
              <p className="whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#0B0F19] border border-[#2A3441] rounded-xl px-4 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-[#0099CC]" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-4 border-t border-[#2A3441]">
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)}
            placeholder="Posez votre question..."
            className="bg-[#0B0F19] border-[#2A3441] text-white flex-1"
            data-testid="guide-ai-input" />
          <Button type="submit" disabled={loading || !input.trim()}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
            data-testid="guide-ai-send">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function GuidePage() {
  const { isAdmin } = useAuth();
  const [activeModule, setActiveModule] = useState(null);
  const [activeTab, setActiveTab] = useState("modules"); // modules | ai
  const [searchQuery, setSearchQuery] = useState("");

  const userRole = isAdmin ? "admin" : "client";

  const roleFilteredModules = MODULES.filter(m => m.roles.includes(userRole));

  const filteredModules = roleFilteredModules.filter(m =>
    !searchQuery || m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.steps.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (activeModule) {
    const mod = MODULES.find(m => m.id === activeModule);
    if (mod) return <ModuleDetail mod={mod} onBack={() => setActiveModule(null)} />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="guide-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-[#0099CC]" />
            Centre d'aide
          </h1>
          <p className="text-[#94A3B8] mt-1">
            Guide complet et assistant IA pour maitriser GLOTECH OS
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button size="sm"
          variant={activeTab === "modules" ? "default" : "outline"}
          onClick={() => setActiveTab("modules")}
          className={activeTab === "modules" ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
          data-testid="tab-modules">
          <BookOpen className="h-3.5 w-3.5 mr-1.5" />Modules
        </Button>
        <Button size="sm"
          variant={activeTab === "ai" ? "default" : "outline"}
          onClick={() => setActiveTab("ai")}
          className={activeTab === "ai" ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
          data-testid="tab-ai">
          <Bot className="h-3.5 w-3.5 mr-1.5" />Assistant IA
        </Button>
      </div>

      {activeTab === "modules" && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher dans le guide..."
              className="bg-[#151B2B] border-[#2A3441] text-white pl-10"
              data-testid="guide-search" />
          </div>

          {/* Workflow overview - admin only */}
          {isAdmin && (
          <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#0099CC]" />
              Workflow commercial complet
            </h3>
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {["Lead", "Proposition", "Envoi email", "E-signature", "Client (auto)", "Contrat (auto)", "Projet (auto)", "Facture (auto)", "Paiement Stripe"].map((step, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3 text-[#64748b]" />}
                  <span className="bg-[#0099CC]/10 text-[#0099CC] px-2 py-0.5 rounded-full text-xs font-medium">{step}</span>
                </span>
              ))}
            </div>
          </div>
          )}

          {/* Module grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredModules.map(mod => (
              <ModuleCard key={mod.id} mod={mod} onClick={() => setActiveModule(mod.id)} />
            ))}
          </div>

          {filteredModules.length === 0 && (
            <div className="text-center py-12 bg-[#151B2B] rounded-xl border border-[#2A3441]">
              <Search className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
              <p className="text-white font-medium">Aucun resultat</p>
              <p className="text-[#94A3B8] text-sm mt-1">Essayez un autre terme de recherche</p>
            </div>
          )}
        </>
      )}

      {activeTab === "ai" && <AiChat />}
    </div>
  );
}
